import React, { useEffect, useRef } from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import CandidateCardNew from "bundles/common/components/CandidateCardNew";
import theme from "../../../../../Layouts/theme";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useIntl } from "react-intl";

import { draggable, dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import { attachClosestEdge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";

const KanbanCard = ({
  card,
  columnId,
  columnKey,
  index,
  dragDisabled,
  draggingCardId,
  advancing = false,
  onMoveForward,
  moveForwardLabel = null,
  favorites,
  onToggleFavorite,
  onOpenDialog,
  onJobApplicationClosed,
  onKanbanMetaUpdate,
}) => {
  const intl = useIntl();
  
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const ref = useRef(null);

  const staleBadge = (() => {
    const ts = card?.last_moved_at;
    if (columnKey === "hired") return null;
    if (!ts) return null;
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return null;

    const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
    if (days >= 14) {
      return {
        label: "14" + intl.formatMessage({ id: "recruiter.kanban.days_short" }) + "+",
        tooltipDays: 14,
        bg: theme.palette.primary.main,
        border: theme.palette.primary.main,
        color: theme.palette.common.white,
      };
    }
    if (days >= 7) {
      return {
        label: "7" + intl.formatMessage({ id: "recruiter.kanban.days_short" }) + "+",
        tooltipDays: 7,
        bg: theme.palette.warning.main,
        border: theme.palette.warning.main,
        color: theme.palette.secondary.black,
      };
    }

    return null;
  })();

  useEffect(() => {
    if (dragDisabled) return undefined;
    const el = ref.current;
    if (!el) return undefined;

    return combine(
      draggable({
        element: el,
        getInitialData: () => ({
          type: "kanban-card",
          cardId: card.id,
          fromColumnId: Number(columnId),
        }),
      }),
      dropTargetForElements({
        element: el,
        canDrop: ({ source }) => source?.data?.type === "kanban-card",
        getData: ({ input, element }) =>
          attachClosestEdge(
            {
              type: "kanban-card-target",
              columnId: Number(columnId),
              index,
            },
            {
              input,
              element,
              allowedEdges: ["top", "bottom"],
            }
          ),
      })
    );
  }, [card.id, columnId, dragDisabled, index]);

  const candidateForCard = {
    ...(card?.candidate || {}),
    profile_picture_url: card?.candidate?.profile_picture_url,
    sent_from_talent_team: card?.sent_from_talent_team,
    is_external: Boolean(card?.is_external || card?.candidate?.is_external),
  };

  const jobApplications = card?.job_applications || [];

  return (
    <Box
      ref={ref}
      sx={{
        position: "relative",
        cursor: dragDisabled ? "default" : "grab",
        boxShadow: "none",
        userSelect: "none",
        opacity: draggingCardId === card.id ? 0.6 : 1,
      }}
    >
      {staleBadge && isDesktop ? (
        <Tooltip
          title={intl.formatMessage(
            { id: "recruiter.kanban.stale_card_tooltip" },
            { count: staleBadge.tooltipDays }
          )}
          arrow
          placement="top"
        >
          <Box
            sx={{
              position: "absolute",
              top: -12,
              left: 16,
              zIndex: 1,
              backgroundColor: staleBadge.bg,
              border: `1px solid ${staleBadge.border}`,
              borderRadius: "999px",
              px: "8px",
              py: "2px",
            }}
          >
            <Typography sx={{ fontSize: "11px", fontWeight: 700, fontFamily: "Segoe UI Semibold, sans-serif", color: staleBadge.color, opacity: 0.9 }}>
              {staleBadge.label}
            </Typography>
          </Box>
        </Tooltip>
      ) : null}

      <CandidateCardNew
        candidate={candidateForCard}
        jobApplications={jobApplications}
        showCloseIcon={false}
        chipVariant="team"
        pinSaveIconToBottom={true}
        showMetaChips={false}
        favorites={favorites || []}
        showSaveIcon={!candidateForCard?.is_external}
        handleFavoriteToggle={(candidateId, action) => {
          if (candidateForCard?.is_external) return;
          if (action !== "toggle") return;
          if (!candidateId) return;
          if (typeof onToggleFavorite === "function") onToggleFavorite(candidateId);
        }}
        onActionButtonClick={() => {
          if (typeof onOpenDialog === "function") onOpenDialog(card, candidateForCard);
        }}
        onJobApplicationClosed={(closed) => {
          if (typeof onJobApplicationClosed === "function") onJobApplicationClosed(closed, card, candidateForCard);
        }}
        onKanbanMetaUpdate={onKanbanMetaUpdate}
        columnKey={columnKey}
        secondaryActionButtonLabel={columnKey === "pending" ? moveForwardLabel : null}
        onSecondaryActionButtonClick={
          columnKey === "pending"
            ? () => {
              if (typeof onMoveForward === "function") onMoveForward(card);
            }
            : null
        }
        secondaryActionDisabled={dragDisabled || advancing || !moveForwardLabel}
      />
    </Box>
  );
};

export default KanbanCard;


